import { useCallback } from 'react';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useGetActiveSequenceMetadataQuery } from '@frameio/shared/integrations/gql';
import { convertSecondsToMicroseconds } from '@frameio/utils/media/media';
import { useActiveDocumentId } from '../../shared/contexts/ActiveHostEntitiesContext';
import { useSetSequencePositionMigrationMutation } from './markerSync';
import {
  findExportMetadataForAsset,
  shouldApplySequenceInOffset,
} from './markerSync/commentMarkerSyncConfig';

/**
 * React hook that sets the Premiere timeline playhead when the user selects a
 * comment timestamp in the media viewer.
 *
 * Adjusts timestamps using active sequence export metadata (sequence in point)
 * when applicable, using the same export-metadata helpers as marker sync.
 *
 * @returns Object containing the `setSequencePosition` function
 */
export const useSetSequencePosition = () => {
  const isPremiereClient = useIsMatchingClientName('premiere');
  const sequenceId = useActiveDocumentId();
  const { setPlayheadFromComment } = useSetSequencePositionMigrationMutation();
  const { data: sequenceMetadataData } = useGetActiveSequenceMetadataQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'network-only',
    skip: !isPremiereClient,
  });

  const metadata =
    sequenceMetadataData?.activeProject?.activeSequence?.metadata;

  const setSequencePosition = useCallback(
    ({
      commentId,
      timestampMicroseconds,
      assetId,
    }: {
      commentId: string;
      timestampMicroseconds: number;
      assetId?: string | null;
    }) => {
      if (!assetId) {
        return;
      }

      const exportMetadata = findExportMetadataForAsset({
        assetId,
        premiereSequenceMetadata: metadata,
      });

      if (!exportMetadata) {
        return;
      }

      const applySequenceInOffset = shouldApplySequenceInOffset(exportMetadata);

      const adjustedTimestamp =
        applySequenceInOffset &&
        'sequenceIn' in exportMetadata &&
        exportMetadata.sequenceIn != null
          ? timestampMicroseconds +
            (convertSecondsToMicroseconds(exportMetadata.sequenceIn) || 0)
          : timestampMicroseconds;

      setPlayheadFromComment({
        assetId,
        commentId,
        documentId: sequenceId,
        timestampMicroseconds: adjustedTimestamp,
      });
    },
    [metadata, sequenceId, setPlayheadFromComment]
  );

  return { setSequencePosition };
};
